import { Injectable } from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  AsyncValidatorFn,
  FormArray,
  FormBuilder as NgFormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';

import { Observable } from 'rxjs';

export type FormControlStatus = FormControl['status'];
/* eslint-disable @typescript-eslint/no-unused-vars */

type Omit<T, K> = Pick<T, Exclude<keyof T, K>>;
export interface AbstractControlTyped<T> extends AbstractControl {
  readonly value: T;
  valueChanges: Observable<T>;
  readonly status: FormControlStatus;
  statusChanges: Observable<FormControlStatus>;
  get: <V = unknown>(path: (string | number)[] | string) => AbstractControlTyped<V> | null;
  setValue: <V>(
    value: V extends T ? V : never,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ) => void;
  patchValue: <V>(
    value: V extends Partial<T> ? V : never,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ) => void;
  reset: <V>(
    value?: V extends Partial<T> ? V : never,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ) => void;
}
export type FormControlTypedValue<T> = T extends (infer V)[] ? Exclude<V, ValidatorFn | null> : T;
export interface FormControlTyped<T> extends FormControl {
  readonly value: FormControlTypedValue<T>;
  valueChanges: Observable<T>;
  readonly status: FormControlStatus;
  statusChanges: Observable<FormControlStatus>;
  get: <V = unknown>(path: (string | number)[] | string) => AbstractControlTyped<V> | null;
  setValue: (
    value: FormControlTypedValue<T>,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ) => void;
  patchValue: <V>(
    value: V extends Partial<T> ? V : never,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ) => void;
  reset: <V>(
    value?: V extends Partial<T> ? V : never,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ) => void;
}
export type ExtractArrayType<T> = T extends (infer V)[] ? V : T;
export type FormGroupInputType = Record<string, [any, ValidatorFn] | FormControlTyped<any> | any>;
// prettier-ignore
export type FormGroupValue<T extends FormGroupInputType> = {
  [P in keyof T]:
    T[P] extends [infer V1, any] ? FormControlTyped<Exclude<V1, ValidatorFn | null>>['value'] :
    T[P] extends FormControlTyped<infer V2> ? FormControlTyped<V2>['value'] :
    T[P] extends FormGroupTyped<infer V3> ? FormGroupTyped<V3>['value'] :
    T[P] extends FormArrayTyped<infer V4> ? FormArrayTyped<V4>['value'] :
    FormControlTyped<T[P]>['value']
}
// prettier-ignore
type FormGroupControls<T extends FormGroupInputType> = {
  [P in keyof T]:
    T[P] extends (infer V1)[] ? FormControlTyped<Exclude<V1, ValidatorFn | null>> :
    T[P] extends FormControlTyped<infer V2> ? FormControlTyped<V2> :
    T[P] extends FormGroupTyped<infer V3> ? FormGroupTyped<V3> :
    T[P] extends FormArrayTyped<infer V4> ? FormArrayTyped<V4> :
    FormControlTyped<T[P]>
}
// prettier-ignore
export type FormArrayTypedValue<T> = (
      T extends FormGroupTyped<infer V1> ? FormGroupTyped<V1>['value'] :
      T extends FormControlTyped<infer V2> ? FormControlTyped<V2>['value'] :
      { [P in keyof T]:
          T[P] extends (infer V3)[] ? Exclude<V3, ValidatorFn> :
          T[P] extends FormControlTyped<infer V4> ? FormControlTyped<V4>['value'] :
          T[P] extends FormGroupTyped<infer V5> ? FormGroupTyped<V5>['value'] :
          T[P] extends FormArrayTyped<infer V6> ? FormArrayTyped<V6>['value']:
          FormControlTyped<T[P]>['value']
      }
    )[]

// prettier-ignore
export type FormArrayControl<T> =
  T extends FormGroupTyped<infer V1> ? FormGroupTyped<V1> :
  T extends FormControlTyped<infer V2> ? FormControlTyped<V2> :
    {
      [P in keyof T]:
        T[P] extends (infer V3)[] ? FormControlTyped<Exclude<V3, ValidatorFn>> :
        T[P] extends FormControlTyped<infer V4> ? FormControlTyped<V4 extends (infer I)[] ? Exclude<I, ValidatorFn | null> : V4> :
        T[P] extends FormGroupTyped<infer V5> ? FormGroupTyped<V5> :
        T[P] extends FormArrayTyped<infer V6> ? FormArrayTyped<V6> :
        FormControlTyped<T[P]>
}
type OverridenGroupProps = 'controls';
export interface FormGroupTyped<T extends FormGroupInputType>
  extends Omit<FormGroup, OverridenGroupProps> {
  readonly value: FormGroupValue<T>;
  readonly status: FormControlStatus;
  controls: FormGroupControls<T>;
  valueChanges: Observable<FormGroupValue<T>>;
  statusChanges: Observable<FormControlStatus>;
  registerControl: (<P extends keyof T>(
    name: P,
    control: AbstractControlTyped<T[P]>,
  ) => AbstractControlTyped<T[P]>) &
    (<V = any>(name: string, control: AbstractControlTyped<V>) => AbstractControlTyped<V>);
  addControl: (<P extends keyof T>(name: P, control: FormGroupControls<T>[P]) => void) &
    (<V = any>(name: string, control: AbstractControlTyped<V>) => void);
  removeControl: ((name: keyof T) => void) & ((name: string) => void);
  setControl: (<P extends keyof T>(name: P, control: AbstractControlTyped<T[P]>) => void) &
    (<V = any>(name: string, control: AbstractControlTyped<V>) => void);
  contains: ((name: keyof T) => boolean) & ((name: string) => boolean);
  get: (<P extends keyof T>(path: P) => FormGroupControls<T>[P]) &
    (<V = unknown>(path: (string | number)[] | string) => AbstractControlTyped<V> | null);
  getRawValue: () => FormGroupValue<T> & { [disabledProp in string | number]: any };
  setValue: <V>(
    value: FormGroupValue<T>,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ) => void;
  patchValue: <V>(
    value: Partial<FormGroupValue<T>>,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ) => void;
  reset: <V>(
    value?: Partial<FormGroupValue<T>>,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ) => void;
}
export type OverridenArrayProps = 'controls' | 'at' | 'push' | 'insert' | 'setControl';
export interface FormArrayTyped<U, T = ExtractArrayType<U>>
  extends Omit<FormArray, OverridenArrayProps> {
  readonly status: FormControlStatus;
  readonly value: FormArrayTypedValue<T>;
  controls: FormArrayControl<T>[];
  valueChanges: Observable<FormArrayTypedValue<T>>;
  statusChanges: Observable<FormControlStatus>;
  at: (index: number) => FormArrayControl<T>;
  push: (ctrl: FormArrayControl<T>) => void;
  insert: (index: number, control: FormArrayControl<T>) => void;
  setControl: (index: number, control: FormArrayControl<T>) => void;
  getRawValue: () => FormArrayTypedValue<T>;
  get: <V = unknown>(path: (string | number)[] | string) => AbstractControlTyped<V> | null;
  setValue: <V>(
    value: FormArrayTypedValue<T>,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ) => void;
  patchValue: <V>(
    value: FormArrayTypedValue<T>,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ) => void;
  reset: <V>(
    value?: FormArrayTypedValue<T>,
    options?: { onlySelf?: boolean; emitEvent?: boolean },
  ) => void;
}
@Injectable({ providedIn: 'root' })
export class TypedFormBuilder extends (<any>NgFormBuilder) {
  public control<V>(
    formState: V,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null,
  ): FormControlTyped<V> {
    return <FormControlTyped<V>>super.control(formState, validatorOrOpts, asyncValidator);
  }
  public group<T extends FormGroupInputType>(
    controlsConfig: T,
    options?: AbstractControlOptions,
  ): FormGroupTyped<T> {
    return <any>(<unknown>super.group(controlsConfig, options));
  }
  public array<
    T extends Record<
      string,
      [any, ValidatorFn] | FormControlTyped<any> | FormGroupTyped<any> | FormArrayTyped<any> | any
    >[],
  >(
    controlsConfig: T,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null,
  ): FormArrayTyped<T> {
    return <FormArrayTyped<T>>(
      (<unknown>super.array(controlsConfig, validatorOrOpts, asyncValidator))
    );
  }
}
